import { FC, Fragment, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "store/hooks";
import InboxNotificationsBell from "components/InboxNotificationsBell/InboxNotificationsBell";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface HeaderProps {
  crumbs?: { text: string; link?: string }[];
}

const Header: FC<HeaderProps> = ({ crumbs }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [homeSection, setHomeSection] = useState("Workflows");

  useEffect(() => {
    const updateHomeSection = (event: Event) => {
      const label = (event as CustomEvent<{ label?: string }>).detail?.label;
      if (label) setHomeSection(label);
    };
    window.addEventListener("leadstats:home-section", updateHomeSection);
    return () =>
      window.removeEventListener("leadstats:home-section", updateHomeSection);
  }, []);

  const handleLogout = () => {
    const wasSSO = localStorage.getItem("loggedInViaSSO") === "true";
    const returnOrigin = localStorage.getItem("ssoReturnOrigin");
    localStorage.removeItem("userData");
    localStorage.removeItem("loggedInViaSSO");
    localStorage.removeItem("ssoReturnOrigin");
    document.cookie = "";
    if (wasSSO) {
      const fallback =
        process.env.REACT_APP_LEADSTATS_URL || "https://app.leadstats.io";
      window.location.href =
        returnOrigin && /^https?:\/\//.test(returnOrigin)
          ? returnOrigin
          : fallback;
      return;
    }
    window.location.reload();
  };

  const { firstName, lastName, email } = useAppSelector(
    (state) => state.auth.userData
  );
  const displayName = firstName || lastName || email;
  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((value) => value?.trim().charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const fallbackInitials = email?.slice(0, 2).toUpperCase() || "LS";
  const isHome = location.pathname === "/" || location.pathname === "/home";
  const visibleCrumbs =
    crumbs && crumbs.length > 0
      ? crumbs
      : isHome
      ? [{ text: "Home" }, { text: homeSection }]
      : [{ text: "Workspace" }];

  return (
    <header className="relative z-[90] flex h-[60px] min-h-[60px] w-full items-center justify-between border-b border-themed-border bg-themed-header px-6 font-manrope">
      <nav
        className="flex min-w-0 select-none items-center gap-2 text-[13px] font-medium leading-[20px] text-themed-text"
        aria-label="Breadcrumb"
      >
        {visibleCrumbs.map((crumb, i) => (
          <Fragment key={i}>
            <button
              type="button"
              className={`truncate ${
                i === visibleCrumbs.length - 1
                  ? "text-themed-text"
                  : "cursor-pointer text-themed-textSecondary hover:text-themed-text"
              }`}
              onClick={() => {
                if (crumb.link) navigate(crumb.link);
              }}
              disabled={!crumb.link}
            >
              {crumb.text}
            </button>
            {i !== visibleCrumbs.length - 1 && (
              <span className="text-themed-textSecondary opacity-40">/</span>
            )}
          </Fragment>
        ))}
      </nav>
      <div className="flex items-center gap-3">
        <InboxNotificationsBell showLabel />
        {displayName && (
          <>
            <span className="h-[22px] w-px bg-themed-border" />
            <Menu as="div" className="relative z-auto">
              <div>
                <Menu.Button className="flex h-[34px] max-w-[300px] items-center gap-2 rounded-lg border border-transparent bg-transparent py-1 pl-1 pr-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-themed-ring hover:bg-themed-elevated">
                  <span className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-md bg-themed-accentSoft text-[10px] font-bold text-themed-text">
                    {initials || fallbackInitials}
                  </span>
                  <span className="hidden truncate text-[12px] font-semibold leading-[20px] text-themed-text 2xl:block">
                    <span className="sr-only">Open user menu for </span>
                    {displayName}
                  </span>
                  <ChevronDownIcon
                    className="hidden h-4 w-4 flex-shrink-0 text-themed-textSecondary 2xl:block"
                    aria-hidden="true"
                  />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-[120] mt-2 w-48 origin-top-right overflow-hidden rounded-lg border border-themed-border bg-themed-card py-1 shadow-2xl focus:outline-none">
                  <Menu.Item>
                    {({ active }) => (
                      <Link to="/settings">
                        <div
                          className={classNames(
                            active ? "bg-themed-hover" : "",
                            "block px-4 py-2 text-sm text-themed-text"
                          )}
                        >
                          Settings
                        </div>
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <div
                        className={classNames(
                          active ? "bg-themed-hover" : "",
                          "block px-4 py-2 text-sm text-themed-text cursor-pointer"
                        )}
                        onClick={handleLogout}
                      >
                        Logout
                      </div>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
